import { useCallback, useEffect, useRef } from 'react';

import {
  AutosuggestionContainer,
  AutosuggestionIcon,
  AutosuggestionItem,
  AutosuggestionList,
  AutosuggestionTitle,
} from 'components/DeepSearch/components/components';
import { PlaceholderItem } from 'components/DeepSearch/types/chatTypes';

interface AutosuggestionPromptsProps {
  isVisible: boolean;
  items: PlaceholderItem[];
  title?: string;
  // Opt-in: smooth-scroll the panel into view when it opens. Helps on small
  // screens where it would otherwise open below the fold. Off by default.
  scrollIntoViewOnOpen?: boolean;
  onSelectPrompt: (
    suggestionMainText: string,
    suggestionExampleText: string,
    type: string
  ) => void;
  onClose: () => void;
}

export const AutosuggestionPrompts = ({
  isVisible,
  items,
  title = 'You might ask',
  scrollIntoViewOnOpen = false,
  onSelectPrompt,
  onClose,
}: AutosuggestionPromptsProps) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    },
    [onClose]
  );

  useEffect(() => {
    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isVisible, handleClickOutside]);

  useEffect(() => {
    if (!isVisible || !scrollIntoViewOnOpen) {
      return;
    }
    // Defer so the open transition + on-screen keyboard have settled before we
    // scroll the panel into view.
    const timer = setTimeout(() => {
      containerRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }, 150);

    return () => clearTimeout(timer);
  }, [isVisible, scrollIntoViewOnOpen]);

  const handlePromptClick = (item: PlaceholderItem) => {
    onSelectPrompt(
      item.suggestionMainText,
      item.suggestionExampleText,
      item.searchType
    );
    onClose();
  };

  return (
    <AutosuggestionContainer
      ref={containerRef}
      className={isVisible ? 'visible' : ''}
    >
      <AutosuggestionTitle>{title}</AutosuggestionTitle>
      <AutosuggestionList>
        {items.map(
          (item, index) =>
            item?.suggestionTitle && (
              <AutosuggestionItem
                key={index}
                onClick={() => handlePromptClick(item)}
              >
                <AutosuggestionIcon />
                {item.suggestionTitle}
              </AutosuggestionItem>
            )
        )}
      </AutosuggestionList>
    </AutosuggestionContainer>
  );
};
